import {useState} from 'react';

import classNames from 'classnames';

import type {Tab} from 'shells/control-bar/page-control-bar';
import {Stack} from 'shells/layout/stack/stack';
import {VerticalTabs} from 'shells/tabs/vertical-tabs/vertical-tabs';

import {useScrollBasedTabSelection} from '../hooks/use-scroll-based-tab-selection';
import {useScrollToTab} from '../hooks/use-scroll-to-tab';

import {PinnedExpandButton} from './pinned-expand-button';

import styles from './workspace-section.module.css';

export type WorkspaceTab = Tab;

interface Props {
    tabs: WorkspaceTab[];
    widgets: React.ReactNode[];
    isCollapsed: boolean;
    onIsCollapsedChange: (isCollapsed: boolean) => void;
    workspaceSectionRef: React.RefObject<HTMLDivElement>;
    scrollContainerRef?: HTMLDivElement | null | undefined;
    tabRefs: Record<string, React.RefObject<HTMLDivElement>>;
    stickyHeaderOffset?: number;
    hasFullHeightSidebar?: boolean;
}

export function WorkspaceSection(props: Props) {
    const {
        tabs,
        widgets,
        isCollapsed,
        onIsCollapsedChange,
        workspaceSectionRef,
        scrollContainerRef,
        tabRefs,
        stickyHeaderOffset = 0,
        hasFullHeightSidebar,
    } = props;

    const [selectedTab, setSelectedTab] = useState<string>(tabs[0]?.id || 'overview');

    useScrollBasedTabSelection({
        tabRefs,
        scrollContainerRef,
        selectedTab,
        onTabChange: setSelectedTab,
    });

    const {scrollToTab} = useScrollToTab({
        tabRefs,
        scrollContainerRef: scrollContainerRef ? {current: scrollContainerRef} : null,
        stickyHeaderOffset,
    });

    const handleTabChange = (tabId: string) => {
        setSelectedTab(tabId);
        scrollToTab(tabId);
    };

    return (
        <div ref={workspaceSectionRef} className={styles['workspace-section']}>
            <div className={styles['navigation-tabs-container']}>
                <VerticalTabs
                    tabs={{
                        options: tabs,
                        selectedTab: selectedTab,
                        onChangeTab: handleTabChange,
                    }}
                    iconOnly={isCollapsed}
                />
                <PinnedExpandButton isCollapsed={isCollapsed} onStateChange={onIsCollapsedChange} />
            </div>
            <div
                className={classNames(styles['content'], {
                    [styles['content--full-height-sidebar']]: hasFullHeightSidebar,
                })}
            >
                <Stack spacing={12} isFullWidth={true}>
                    {widgets
                        .filter((widget) => widget)
                        .map((widget) => {
                            const widgetId = (widget as React.ReactElement).key as string;
                            return (
                                <div key={widgetId} ref={tabRefs[widgetId]} data-tab-id={widgetId}>
                                    {widget}
                                </div>
                            );
                        })}
                </Stack>
            </div>
        </div>
    );
}
